
import { ReactNode, useState, useEffect, lazy, Suspense } from 'react';
import Sidebar from './Sidebar';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import FloatingWhatsAppButton from '../ui/FloatingWhatsAppButton';
import { ThemeProvider } from '@/components/theme/ThemeProvider';
import { Toaster } from '@/components/ui/toaster';
import { useIsMobile } from '@/hooks/use-mobile';
import { ErrorBoundary } from '@/components/ui/error-boundary';
import { SeshBalance } from '@/components/sesh/SeshBalance';
import { supabase } from '@/integrations/supabase/client';
import { cn } from "@/lib/utils";
import { useResponsiveTouchTargets } from '@/lib/mobile-utils';
import { useSidebarPersistence } from '@/hooks/useSidebarPersistence';

// Lazy load non-critical components
const LazyBreadcrumbNav = lazy(() => import('./BreadcrumbNav'));

interface MainLayoutProps {
  children: ReactNode;
  showSidebar?: boolean;
}

const MainLayoutContent = ({ children, showSidebar = true }: MainLayoutProps) => {
  const isMobile = useIsMobile();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { touchFeedback } = useResponsiveTouchTargets();

  // Use o hook de persistência da sidebar
  useSidebarPersistence();

  useEffect(() => {
    // Check if user is authenticated
    supabase.auth.getSession().then(({ data }) => {
      setIsAuthenticated(!!data.session);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setIsAuthenticated(!!session);
    });

    // Core Web Vitals optimization - only show content when ready
    const timer = setTimeout(() => {
      setIsLoading(false);
      // Report LCP (Largest Contentful Paint) for Core Web Vitals
      if (window.performance && 'PerformanceObserver' in window) {
        // Attempt to report metrics to console for debugging
        try {
          const observer = new PerformanceObserver((list) => {
            const entries = list.getEntries();
            entries.forEach((entry) => {
              if (entry.entryType === 'largest-contentful-paint') {
                console.log(`LCP: ${entry.startTime}ms`);
              }
            });
          });
          observer.observe({ type: 'largest-contentful-paint', buffered: true });
        } catch (e) {
          console.error('Performance observer error:', e);
        }
      }
    }, 100);
    return () => {
      clearTimeout(timer);
      subscription?.unsubscribe();
    };
  }, []);

  if (isLoading) {
    return (
      <div className="flex min-h-screen w-full bg-background items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-blue-500 rounded-full border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen w-full bg-background">
      {showSidebar && (
        <ErrorBoundary
          fallback={<div className="w-16 bg-sidebar h-screen"></div>}
          componentName="Sidebar"
        >
          <Sidebar />
        </ErrorBoundary>
      )}
      <main className="flex-1 overflow-x-hidden">
        <div className={cn(
          "flex items-center justify-between border-b relative z-20",
          isMobile ? "p-3" : "p-4"
        )}>
          <div className="flex items-center">
            {showSidebar && (
              <div className="relative z-30 touch-action-manipulation">
                <SidebarTrigger
                  className={cn(
                    "flex items-center justify-center focus:ring-2 focus:ring-primary focus:ring-offset-2 hover:bg-gray-100 relative mr-2 md:mr-3",
                    isMobile ? "h-[56px] w-[56px]" : "h-10 w-10",
                    touchFeedback
                  )}
                  aria-label="Toggle sidebar"
                />
              </div>
            )}
            <h1 className={cn(
              "ml-1 font-medium",
              isMobile ? "text-lg" : "text-base md:text-lg"
            )}>vive calme</h1>
          </div>
          {isAuthenticated && (
            <SeshBalance variant="header" className="ml-auto" />
          )}
        </div>
        <div className={cn(
          "overflow-y-auto",
          isMobile ? "p-4" : "p-5"
        )}>
          {/* Add breadcrumb navigation for better internal linking */}
          <Suspense fallback={<div className="h-6 mb-4"></div>}>
            <ErrorBoundary fallback={<div className="h-6 mb-4"></div>} componentName="BreadcrumbNav">
              <LazyBreadcrumbNav />
            </ErrorBoundary>
          </Suspense>
          <ErrorBoundary
            fallback={
              <div className="p-4 border border-red-200 rounded-md bg-red-50 text-red-700">
                <h3 className="font-medium">Error loading content</h3>
                <p className="text-sm mt-1">Please refresh the page or try again later.</p>
              </div>
            }
            componentName="PageContent"
          >
            {children}
          </ErrorBoundary>
        </div>
      </main>
    </div>
  );
};

const MainLayout = ({ children, showSidebar = true }: MainLayoutProps) => {
  return (
    <ErrorBoundary
      fallback={<div className="p-4 text-center">Error loading layout. Please refresh.</div>}
      onError={(error) => console.error("Layout error:", error)}
      componentName="MainLayout"
    >
      <ThemeProvider defaultTheme="light" storageKey="vive-calme-theme">
        <SidebarProvider>
          <MainLayoutContent showSidebar={showSidebar}>
            {children}
          </MainLayoutContent>
          <FloatingWhatsAppButton />
          <Toaster />
        </SidebarProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default MainLayout;
